import { memo, useEffect } from 'react';
import { RoomAudioRenderer, useMaybeRoomContext } from '@livekit/components-react';
import { BlockLink } from 'BlockLink';
import { RoomEvent } from 'livekit-client';
import { useTutorBotBottomSpacing } from 'TutorBotChat';
import { twMerge } from 'tailwind-merge';
import FrontRoyalSpinner from 'FrontRoyalSpinner';
import { useStoreAudioTranscriptions } from '../hooks/useStoreAudioTranscriptions';
import { AudioVisualizer } from './AudioVisualizer';
import { AudioControlBar } from './AudioControlBar/AudioControlBar';
import { useRoomEventListener } from '../hooks/useRoomEventListener';
import { getAlgorithmMetadataEventListener, errorEventListener } from '../utils';
import { useSessionTimer } from '../hooks/useSessionTimer';
import { SessionWillEndSoonMessage } from './SessionWillEndSoonMessage/SessionWillEndSoonMessage';
import '../locales/TutorBotVoiceAgent/TutorBotVoiceAgent-en.json';
import { type SupportedColors, type AudioUiContext } from '../TutorBotVoiceAgent.types';

type Props = {
    uiContext: AudioUiContext;
    conversationId: string;
    color: SupportedColors | undefined;
    abortFromRoom: () => void;
    allowAbortDuringConversation: boolean;
    abortText: string;
};

type PresentationProps = Pick<Props, 'abortFromRoom' | 'abortText' | 'allowAbortDuringConversation'> & {
    audioVisualizer: React.ReactNode;
    sessionTimer: ReturnType<typeof useSessionTimer>;
    audioControlBar: React.ReactNode;
    roomAudioRenderer: React.ReactNode;
};

export function ConnectedLiveKitRoomUiPresentation({
    abortFromRoom,
    allowAbortDuringConversation,
    abortText,
    audioVisualizer,
    sessionTimer,
    audioControlBar,
    roomAudioRenderer,
}: PresentationProps) {
    const spacingBeneathLowestButton = useTutorBotBottomSpacing('h-[90px]');

    const room = useMaybeRoomContext();

    useEffect(() => {
        if (!room) return undefined;

        return () => {
            room.disconnect();
        };
    }, [room]);

    return (
        <>
            {/* This wrapper stretches to fill all the vertical space above the AudioControlBar */}
            <div className="relative flex-grow animate-fade-in [animation-duration:_.6s]">
                {/* The Audio Visualizer also stretches to fill that vertical space */}
                <div className="z-0 h-full">{audioVisualizer}</div>

                {/* The SessionWillEndSoonMessage is fixed to the bottom of the space above the AudioControlBar, and
                 z-indexed in front of the AudioVisualizer */}
                <div className="z-1 absolute bottom-7 w-full">
                    <SessionWillEndSoonMessage sessionTimer={sessionTimer} />
                </div>
            </div>

            {/* The switchToTextMode link is fixed to the bottom of the screen with a height matching spacingBeneathLowestButton.
                The audioControlBar is fixed above that, so it has spacingBeneathLowestButton spacing beneath it. */}
            <div className="relative flex w-full animate-fade-in flex-col [animation-duration:_.6s]">
                {audioControlBar}
                <div className={twMerge('flex w-full items-center justify-center', spacingBeneathLowestButton)}>
                    {allowAbortDuringConversation && (
                        <BlockLink onPress={abortFromRoom} color="slate-grey">
                            {abortText}
                        </BlockLink>
                    )}
                </div>
            </div>
            {roomAudioRenderer}
            {/* <NoAgentNotification /> */}
            {/* We're no longer showing the LiveTranscript in the UI, but you can uncomment this if it's useful for debugging */}
            {/* <LiveTranscript token={connectionDetails?.participantToken} /> */}
        </>
    );
}

// In order to get access to the room context, we split out the UI inside the LiveKitRoom
// into a separate component.
function ConnectedLiveKitRoomUiComponent({
    conversationId,
    uiContext,
    abortText,
    allowAbortDuringConversation,
    abortFromRoom,
    color,
}: Props) {
    useStoreAudioTranscriptions({ conversationId, uiContext });
    useRoomEventListener(
        RoomEvent.DataReceived,
        getAlgorithmMetadataEventListener({ conversationId }),
        errorEventListener,
    );
    const sessionTimer = useSessionTimer({ uiContext });
    const room = useMaybeRoomContext();

    // Our python agent waits for the participant to connect, then proactively publishes the algorithm metadata.
    // But in case the client wasn't listening yet, we also request it here after we detect that the server has
    // entered the room.
    useEffect(() => {
        if (room?.state === 'connected' && Array.from(room?.remoteParticipants.values()).length > 0) {
            room!.localParticipant.publishData(
                new TextEncoder().encode(JSON.stringify({ type: 'requestAlgorithmMetadata' })),
                {
                    reliable: true,
                },
            );
        }
    }, [room, room?.state, room?.remoteParticipants]);

    if (room?.state !== 'connected') return <FrontRoyalSpinner className="centered" />;

    return (
        <ConnectedLiveKitRoomUiPresentation
            audioVisualizer={<AudioVisualizer color={color} />}
            sessionTimer={sessionTimer}
            abortFromRoom={abortFromRoom}
            allowAbortDuringConversation={allowAbortDuringConversation}
            abortText={abortText}
            audioControlBar={<AudioControlBar />}
            roomAudioRenderer={<RoomAudioRenderer />}
        />
    );
}

export const ConnectedLiveKitRoomUi = memo(ConnectedLiveKitRoomUiComponent) as typeof ConnectedLiveKitRoomUiComponent;
